import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../config/axios';

interface InventoryItem {
  inventory_id: number;
  branch_id: number;
  branch_name: string;
  medicine_id: number;
  medicine_name: string;
  category_name: string;
  quantity_available: number;
  min_stock_level: number;
  last_updated: string;
}

interface InventoryState {
  items: InventoryItem[];
  loading: boolean;
  error: string | null;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

const initialState: InventoryState = {
  items: [],
  loading: false,
  error: null,
  pagination: {
    current: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  },
};

export const fetchInventory = createAsyncThunk(
  'inventory/fetchInventory',
  async (params: { page?: number; limit?: number; search?: string; branch_id?: string; category_id?: string; branchId?: number } = {}) => {
    const { page = 1, limit = 10, search = '', branch_id = '', category_id = '', branchId } = params;
    const queryParams: any = { page, limit, search, branch_id, category_id };
    if (branchId) queryParams.branch_id = branchId;
    
    const response = await apiClient.get('/inventory', { params: queryParams });
    return response.data;
  }
);

export const updateStock = createAsyncThunk(
  'inventory/updateStock',
  async ({ inventoryId, quantity }: { inventoryId: number; quantity: number }) => {
    const response = await apiClient.put(`/inventory/${inventoryId}`, { quantity });
    return response.data;
  }
);

const inventorySlice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchInventory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInventory.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchInventory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch inventory';
      })
      .addCase(updateStock.fulfilled, (state, action) => {
        const index = state.items.findIndex(item => item.inventory_id === action.payload.inventory_id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      });
  },
});

export default inventorySlice.reducer;
